import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { operationsAPI, orderAPI } from '../../services/api';
import {
  HiOutlineCube, HiOutlineClipboardCheck, HiOutlineShoppingBag, HiOutlinePlus,
  HiOutlineClipboardList, HiOutlineRefresh, HiOutlineSave, HiOutlineSearch,
  HiOutlineCheckCircle, HiOutlineX, HiOutlineUserGroup, HiOutlinePencil, HiOutlineTrash,
  HiOutlineTruck, HiOutlineReceiptTax
} from 'react-icons/hi';

import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import { useAuth } from '../../context/AuthContext';
import SalesPipelineTab from './SalesPipelineTab';
import DispatchTab from '../store/DispatchTab';
import InvoiceGenerator from '../finance/InvoiceGenerator';
import { generateMergedProductName, formatDragChainOrderCode, formatMountingBracketCode } from '../../utils/productNameHelper';
import { formatBuildProcessSpecs } from '../../utils/buildProcessSpecs';

// Clean incremental radii list in steps of 5: 5, 10, 15, 20 ... up to 700
const BENDING_RADII_OPTIONS = Array.from({ length: 140 }, (_, i) => String((i + 1) * 5));



const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);

const isValidModelNumber = (modelNumber) => {

  if (!modelNumber || typeof modelNumber !== 'string') return false;
  const str = modelNumber.trim();
  if (!str || !isNaN(Number(str))) return false;
  const lower = str.toLowerCase();
  const excludeWords = [
    'gland', 'clamp', 'nut', 'lock', 'total mtr', 'end part', 'angular', 
    'divider', 'upper part', 'end connector', 'mix', 'small lock', 'strip lock'
  ];
  if (excludeWords.some(w => lower.includes(w))) return false;
  return /^(H[-0-9]|10X|15X|20X|21\.1|22X|24\.|25X|25\/|25-|2627\.|3435\.|35X|35XR|42\.1|42X|45X|45XR|56\.1|60X|60XR)/i.test(str);
};

const SalesDashboard = () => {

  const { user } = useAuth();
  const isDragChain = user?.organizationId?.subIndustry === 'Drag Chain' || !user?.organizationId?.subIndustry;
  const [activeTab, setActiveTab] = useState('store_stock'); // 'store_stock', 'shortages_buy', 'place_orders', 'qc_verification'
  
  const [inventory, setInventory] = useState([]);
  const [buildJobs, setBuildJobs] = useState([]);
  const [qualityLogs, setQualityLogs] = useState([]);
  const [sbsLogs, setSbsLogs] = useState([]);
  const [orderShortages, setOrderShortages] = useState([]);
  const [productTemplates, setProductTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  // Sourcing Order form (Unified Flow)
  const [isOrderCustomModel, setIsOrderCustomModel] = useState(false);
  const [availableOrderInnerSizes, setAvailableOrderInnerSizes] = useState([]);
  const [availableOrderRadii, setAvailableOrderRadii] = useState([]);
  const [orderForm, setOrderForm] = useState({
    productName: '',
    itemType: 'finished_good',
    salesPersonName: user?.name || '',
    chainLength: '1000',
    orderQuantity: 1,
    unitPrice: 0,
    unit: 'meters',
    modelNumber: '',
    productSize: '',
    description: '',
    radius: 'N/A',
    designType: 'OP',
    vs: 'N/A',
    hs: 'N/A',
    sr: 'N/A'
  });

  // Inventory Item Modals & Forms (Sales Add Stock Feature)
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [isCustomModel, setIsCustomModel] = useState(false);
  const [itemForm, setItemForm] = useState({
    name: '',
    type: 'finished_good',
    quantity: 0,
    minStock: 100,
    maintainedQuantity: 100,
    unitPrice: 0,
    unit: 'meters',
    location: 'Unassigned',

    size: '',
    width: '',
    description: '',
    modelNumber: '',
    rack: '',
    radius: '',
    chainType: 'OP',
    numberOfLinks: '',
    length: '',
    mountingBracketType: '',
    accessories: '',
    series: 'N/A'
  });

  // Admin Edit Build Job Modal
  const [showEditBuildModal, setShowEditBuildModal] = useState(false);
  const [editingBuildJob, setEditingBuildJob] = useState(null);
  const [editBuildForm, setEditBuildForm] = useState({ productName: '', orderQuantity: 100, status: 'pending', machineName: '', estimatedDate: '' });

  // QC Log Modal (Sales CRUD on QC Logs)
  const [showQcModal, setShowQcModal] = useState(false);
  const [editingQcId, setEditingQcId] = useState(null);
  const [qcForm, setQcForm] = useState({ materialName: '', quantity: 0, unit: 'pcs', remarks: '', qcType: 'inspected', invoiceNumber: '', invoiceUrl: '' });
  const [invoiceFile, setInvoiceFile] = useState(null);

  // Shortage/Buy/Sale Modal (Unified Flow)
  const [showSbsModal, setShowSbsModal] = useState(false);
  const [editingSbsId, setEditingSbsId] = useState(null);
  const [isSbsCustomModel, setIsSbsCustomModel] = useState(false);
  const [availableSbsInnerSizes, setAvailableSbsInnerSizes] = useState([]);
  const [availableSbsRadii, setAvailableSbsRadii] = useState([]);
  const [sbsForm, setSbsForm] = useState({
    type: 'shortage',
    itemName: '',
    itemType: 'finished_good',
    quantity: 0,
    unitPrice: 0,
    unit: 'meters',
    modelNumber: '',
    size: '',
    description: '',
    radius: 'N/A',
    designType: 'OP',
    vs: 'N/A',
    hs: 'N/A',
    sr: 'N/A',
    assignedTo: 'unassigned',
    remarks: ''
  });

  // Inventory CRUD
  const handleSaveItem = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingItemId) {
        await operationsAPI.updateInventoryItem(editingItemId, itemForm);
        toast.success('Inventory item updated');
      } else {
        await operationsAPI.createInventoryItem(itemForm);
        toast.success('Inventory item created');
      }

      // If finished good and model is custom/new, auto-save to product templates
      if (itemForm.modelNumber && itemForm.type === 'finished_good') {
        const exists = productTemplates.some(t => t.modelNumber.toLowerCase() === itemForm.modelNumber.toLowerCase());
        if (!exists) {
          try {
            await operationsAPI.createProductTemplate({
              modelNumber: itemForm.modelNumber,
              name: itemForm.name,
              rack: itemForm.rack || 'Unassigned',
              unit: itemForm.unit,
              innerSize: itemForm.size || 'N/A',
              radius: itemForm.radius || 'N/A',
              designType: itemForm.designType || 'N/A',
              vs: itemForm.vs || 'N/A',
              hs: itemForm.hs || 'N/A',
              sr: itemForm.sr || 'N/A',
              series: itemForm.series || 'N/A'
            });
          } catch (tempErr) {
            console.error('Failed to auto-save product template:', tempErr);
          }
        }
      }

      setShowItemModal(false);
      setItemForm({
        name: '',
        type: 'finished_good',
        quantity: 0,
        minStock: 100,
        maintainedQuantity: 100,
        unitPrice: 0,
        unit: isDragChain ? 'meters' : 'pcs',
        location: 'Unassigned',

        size: '',
        description: '',
        modelNumber: '',
        rack: '',
        radius: 'N/A',
        designType: 'N/A',
        vs: 'N/A',
        hs: 'N/A',
        sr: 'N/A',
        series: 'N/A'
      });
      setEditingItemId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditItem = (item) => {
    const matchesTemplate = productTemplates.some(t => t.modelNumber === item.modelNumber);
    setIsCustomModel(!!item.modelNumber && !matchesTemplate);
    setItemForm({
      name: item.name,
      type: item.type,
      quantity: item.quantity,
      unit: item.unit,
      location: item.location || 'Unassigned',
      size: item.size || '',
      description: item.description || '',
      modelNumber: item.modelNumber || '',
      rack: item.rack || '',
      radius: item.radius || 'N/A',
      designType: item.designType || 'N/A',
      vs: item.vs || 'N/A',
      hs: item.hs || 'N/A',
      sr: item.sr || 'N/A',
      series: item.series || 'N/A'
    });
    setEditingItemId(item._id);
    setShowItemModal(true);
  };

  // Catalog cascading states
  const [availableInnerSizes, setAvailableInnerSizes] = useState([]);
  const [availableRadii, setAvailableRadii] = useState([]);
  const [autoAlmirahOptions, setAutoAlmirahOptions] = useState([]);
  const [autoRackOptions, setAutoRackOptions] = useState([]);
  const [excelAlmirahFound, setExcelAlmirahFound] = useState('');

  const getExcelCodeBeforeX = (excelModelStr) => {
    if (!excelModelStr) return '';
    const s = excelModelStr.trim().toUpperCase();
    const parts = s.split(/[Xx]/);
    const beforeX = parts[0].trim();
    return beforeX.replace(/^H[-_\s]?/, '').trim();
  };

  const getSmartRackAndAlmirahInfo = useCallback((modelQuery, sizeQuery, radiusQuery, selectedAlm = '') => {
    const rawQuery = (modelQuery || sizeQuery || '').trim();
    if (!rawQuery) return { almirahs: [], selectedAlmirah: '', rackOptions: [] };

    const upperInput = rawQuery.toUpperCase();

    const compMatch = upperInput.match(/\d{4,}\.\d+\.\d+|\d{2}\.\d{2}\.\d+/);
    const decMatch = upperInput.match(/\d+\.\d+/);
    const intMatch = upperInput.match(/\d{2,3}/);

    let targetCode = '';
    let isDecimalOrCompound = false;

    if (compMatch) {
      targetCode = compMatch[0];
      isDecimalOrCompound = true;
    } else if (decMatch) {
      targetCode = decMatch[0];
      isDecimalOrCompound = true;
    } else if (intMatch) {
      targetCode = intMatch[0];
      isDecimalOrCompound = false;
    } else {
      targetCode = upperInput;
      isDecimalOrCompound = false;
    }

    let matched = productTemplates.filter(item => {
      const modelStr = (item.modelNumber || item.name || '').toUpperCase();
      return modelStr.includes(targetCode);
    });

    if (matched.length === 0) {
      return { almirahs: [], selectedAlmirah: '', rackOptions: [] };
    }

    const almirahs = Array.from(new Set(matched.map(m => m.almirah || m.location || 'Almirah 1').filter(Boolean)));
    const activeAlmirah = (selectedAlm && almirahs.includes(selectedAlm)) ? selectedAlm : almirahs[0] || 'Almirah 1';

    const rackOptions = Array.from(
      new Set(matched.map(m => m.rack || 'Rack 1').filter(Boolean))
    ).sort();

    return { almirahs, selectedAlmirah: activeAlmirah, rackOptions };
  }, [productTemplates]);

  useEffect(() => {
    if (!itemForm.modelNumber && !itemForm.size) {
      setAutoAlmirahOptions([]);
      setAutoRackOptions([]);
      setExcelAlmirahFound('');
      return;
    }
    const { almirahs, selectedAlmirah, rackOptions } = getSmartRackAndAlmirahInfo(itemForm.modelNumber, itemForm.size, itemForm.radius, itemForm.almirah);
    if (selectedAlmirah) {
      setExcelAlmirahFound(selectedAlmirah);
      setAutoAlmirahOptions(almirahs);
      setAutoRackOptions(rackOptions);
      setItemForm(prev => ({
        ...prev,
        almirah: prev.almirah && almirahs.includes(prev.almirah) ? prev.almirah : selectedAlmirah,
        rack: rackOptions.length > 0 ? (rackOptions.includes(prev.rack) ? prev.rack : rackOptions[0]) : prev.rack
      }));
    } else {
      setExcelAlmirahFound('');
      setAutoAlmirahOptions([]);
      setAutoRackOptions([]);
    }
  }, [itemForm.modelNumber, itemForm.size, itemForm.radius, getSmartRackAndAlmirahInfo]);

  // Auto-generate merged Product Name for itemForm whenever specs change
  useEffect(() => {
    if (itemForm.modelNumber) {
      const mergedName = generateMergedProductName({
        modelNumber: itemForm.modelNumber,
        size: itemForm.size,
        radius: itemForm.radius,
        designType: itemForm.designType,
        vs: itemForm.vs,
        hs: itemForm.hs,
        sr: itemForm.sr
      });
      if (mergedName && mergedName !== itemForm.name) {
        setItemForm(prev => ({ ...prev, name: mergedName }));
      }
    }
  }, [itemForm.modelNumber, itemForm.size, itemForm.radius, itemForm.designType, itemForm.vs, itemForm.hs, itemForm.sr]);

  // Auto-generate merged Product Name for sbsForm whenever specs change
  useEffect(() => {
    if (sbsForm.modelNumber) {
      const mergedName = generateMergedProductName({
        modelNumber: sbsForm.modelNumber,
        size: sbsForm.size,
        radius: sbsForm.radius,
        designType: sbsForm.designType,
        vs: sbsForm.vs,
        hs: sbsForm.hs,
        sr: sbsForm.sr
      });
      if (mergedName && mergedName !== sbsForm.itemName) {
        setSbsForm(prev => ({ ...prev, itemName: mergedName }));
      }
    }
  }, [sbsForm.modelNumber, sbsForm.size, sbsForm.radius, sbsForm.designType, sbsForm.vs, sbsForm.hs, sbsForm.sr]);

  // Auto-generate merged Product Name for orderForm whenever specs change
  useEffect(() => {
    if (orderForm.modelNumber) {
      const mergedName = generateMergedProductName({
        modelNumber: orderForm.modelNumber,
        productSize: orderForm.productSize,
        radius: orderForm.radius,
        designType: orderForm.designType,
        vs: orderForm.vs,
        hs: orderForm.hs,
        sr: orderForm.sr
      });
      if (mergedName && mergedName !== orderForm.productName) {
        setOrderForm(prev => ({ ...prev, productName: mergedName }));
      }
    }
  }, [orderForm.modelNumber, orderForm.productSize, orderForm.radius, orderForm.designType, orderForm.vs, orderForm.hs, orderForm.sr]);

  const catalogModelNumbers = useMemo(() => {
    const dbModels = productTemplates.map(item => item.modelNumber).filter(Boolean);
    return Array.from(new Set(dbModels)).sort();
  }, [productTemplates]);

  const availableWidths = useMemo(() => {
    if (!itemForm.modelNumber) {
      return Array.from(new Set(productTemplates.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
    }
    const matches = productTemplates.filter(t => t.modelNumber === itemForm.modelNumber);
    const widths = Array.from(new Set(matches.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
    return widths.length > 0 ? widths : Array.from(new Set(productTemplates.map(t => t.width || t.innerSize || t.size).filter(Boolean)));
  }, [productTemplates, itemForm.modelNumber]);

  const availableLinks = useMemo(() => {
    if (!itemForm.modelNumber) {
      return Array.from(new Set(productTemplates.map(t => t.numberOfLinks).filter(Boolean)));
    }
    const matches = productTemplates.filter(t => t.modelNumber === itemForm.modelNumber);
    const links = Array.from(new Set(matches.map(t => t.numberOfLinks).filter(Boolean)));
    return links.length > 0 ? links : Array.from(new Set(productTemplates.map(t => t.numberOfLinks).filter(Boolean)));
  }, [productTemplates, itemForm.modelNumber]);

  // Auto-compose spec string for itemForm
  useEffect(() => {
    if (isDragChain && itemForm.modelNumber) {
      const generatedName = formatDragChainOrderCode({
        modelNumber: itemForm.modelNumber,
        width: itemForm.width,
        radius: itemForm.radius,
        chainType: itemForm.chainType,
        numberOfLinks: itemForm.numberOfLinks,
        length: itemForm.length,
        mountingBracketType: itemForm.mountingBracketType,
        accessories: itemForm.accessories
      }) || `Drag Chain ${itemForm.modelNumber}`;

      if (generatedName !== itemForm.name) {
        setItemForm(prev => ({
          ...prev,
          name: generatedName,
          size: prev.width || prev.size
        }));
      }
    }
  }, [
    isDragChain, itemForm.modelNumber, itemForm.width, itemForm.radius,
    itemForm.chainType, itemForm.numberOfLinks, itemForm.length,
    itemForm.mountingBracketType, itemForm.accessories
  ]);

  const handleModelChange = (modelNum) => {
    if (!modelNum) {
      setItemForm(prev => ({
        ...prev,
        modelNumber: '',
        name: '',
        width: '',
        size: '',
        radius: '',
        chainType: 'OP',
        numberOfLinks: '',
        length: '',
        mountingBracketType: '',
        accessories: ''
      }));
      return;
    }
    const match = productTemplates.find(t => t.modelNumber === modelNum);
    const rawR = match?.radius ? String(match.radius).replace(/^R/i, '') : '';
    const widthVal = match?.width || match?.innerSize || '';
    const parts = [
      modelNum,
      widthVal,
      rawR ? `R${rawR}` : '',
      match?.chainType || 'OP',
      match?.numberOfLinks ? `${match.numberOfLinks} Links` : '',
      match?.length,
      match?.mountingBracketType,
      match?.accessories
    ].filter(Boolean);
    const generatedName = `Drag Chain ${parts.join(' · ')}`;

    setItemForm(prev => ({
      ...prev,
      modelNumber: modelNum,
      name: `Drag Chain ${modelNum}`,
      width: '',
      size: '',
      radius: '',
      chainType: match?.chainType || prev.chainType || 'OP',
      numberOfLinks: match?.numberOfLinks || prev.numberOfLinks || '',
      length: match?.length || prev.length || '',
      mountingBracketType: match?.mountingBracketType || prev.mountingBracketType || '',
      accessories: match?.accessories || prev.accessories || '',
      almirah: match?.almirah || prev.almirah || '',
      rack: match?.rack || prev.rack || ''
    }));
  };

  const handleInnerSizeChange = (e) => {
    const selectedInner = e.target.value;
    const match = productTemplates.find(item => item.modelNumber === itemForm.modelNumber && (item.innerSize === selectedInner || item.size === selectedInner));
    if (match) {
      setAvailableRadii(match.radii || []);
      const selectedRadius = match.radii && match.radii.length > 0 ? match.radii[0] : '';
      const mergedName = generateMergedProductName({
        modelNumber: itemForm.modelNumber,
        size: selectedInner,
        radius: selectedRadius,
        designType: match.designType || itemForm.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      });
      setItemForm(prev => ({
        ...prev,
        name: mergedName,
        size: selectedInner,
        description: match.outerSize ? `Outer: ${match.outerSize}` : prev.description,
        radius: selectedRadius,
        designType: match.designType || prev.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      }));
    } else {
      setItemForm(prev => ({ ...prev, size: selectedInner }));
    }
  };

  const handleTypeChange = (e) => {
    const typeVal = e.target.value;
    let defaultUnit = 'pcs';
    if (typeVal === 'raw_material') defaultUnit = 'kg';
    else if (typeVal === 'finished_good') defaultUnit = isDragChain ? 'meters' : 'pcs';
    else if (typeVal === 'hardware') defaultUnit = 'pcs';
    else if (typeVal === 'custom') defaultUnit = 'pcs';

    setItemForm(prev => ({
      ...prev,
      type: typeVal,
      unit: defaultUnit
    }));
  };

  // SBS Log Catalog Handlers
  const handleSbsModelChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setIsSbsCustomModel(true);
      setAvailableSbsInnerSizes([]);
      setAvailableSbsRadii([]);
      setSbsForm(prev => ({ ...prev, modelNumber: '', itemName: '', size: '', radius: '', designType: 'OP', vs: '', hs: '', sr: '' }));
    } else if (val === '') {
      setIsSbsCustomModel(false);
      setAvailableSbsInnerSizes([]);
      setAvailableSbsRadii([]);
      setSbsForm(prev => ({ ...prev, modelNumber: '', itemName: '', size: '', radius: '', designType: 'OP', vs: '', hs: '', sr: '' }));
    } else {
      setIsSbsCustomModel(false);
      const matches = productTemplates.filter(item => item.modelNumber === val);
      const innerSizes = Array.from(new Set(matches.map(m => m.innerSize)));
      const firstEntry = matches[0];
      setAvailableSbsInnerSizes(innerSizes);
      setAvailableSbsRadii([]);
      const autoInner = innerSizes.length === 1 ? innerSizes[0] : '';
      const autoRadius = firstEntry?.radii && firstEntry.radii.length > 0 ? firstEntry.radii[0] : '';
      const autoName = generateMergedProductName({
        modelNumber: val,
        size: autoInner,
        radius: autoRadius,
        designType: firstEntry ? firstEntry.designType : 'OP',
        vs: firstEntry ? firstEntry.vs : '',
        hs: firstEntry ? firstEntry.hs : '',
        sr: firstEntry ? firstEntry.sr : ''
      });
      setSbsForm(prev => ({
        ...prev, modelNumber: val, itemName: autoName, size: autoInner, radius: autoRadius,
        designType: firstEntry ? firstEntry.designType : 'OP', vs: firstEntry ? firstEntry.vs : '', hs: firstEntry ? firstEntry.hs : '', sr: firstEntry ? firstEntry.sr : ''
      }));
    }
  };

  const handleSbsInnerSizeChange = (e) => {
    const selectedInner = e.target.value;
    const match = productTemplates.find(item => item.modelNumber === sbsForm.modelNumber && (item.innerSize === selectedInner || item.size === selectedInner));
    if (match) {
      setAvailableSbsRadii(match.radii || []);
      const selectedRadius = match.radii && match.radii.length > 0 ? match.radii[0] : '';
      const mergedName = generateMergedProductName({
        modelNumber: sbsForm.modelNumber,
        size: selectedInner,
        radius: selectedRadius,
        designType: match.designType || sbsForm.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      });
      setSbsForm(prev => ({
        ...prev,
        itemName: mergedName,
        size: selectedInner,
        description: match.outerSize ? `Outer: ${match.outerSize}` : prev.description,
        radius: selectedRadius,
        designType: match.designType || prev.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      }));
    } else {
      setSbsForm(prev => ({ ...prev, size: selectedInner }));
    }
  };

  // Manufacturing Order Catalog Handlers
  const handleOrderModelChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      setIsOrderCustomModel(true);
      setAvailableOrderInnerSizes([]);
      setAvailableOrderRadii([]);
      setOrderForm(prev => ({ ...prev, modelNumber: '', productName: '', productSize: '', radius: '', designType: 'OP', vs: '', hs: '', sr: '' }));
    } else if (val === '') {
      setIsOrderCustomModel(false);
      setAvailableOrderInnerSizes([]);
      setAvailableOrderRadii([]);
      setOrderForm(prev => ({ ...prev, modelNumber: '', productName: '', productSize: '', radius: '', designType: 'OP', vs: '', hs: '', sr: '' }));
    } else {
      setIsOrderCustomModel(false);
      const matches = productTemplates.filter(item => item.modelNumber === val);
      const innerSizes = Array.from(new Set(matches.map(m => m.innerSize)));
      const firstEntry = matches[0];
      setAvailableOrderInnerSizes(innerSizes);
      setAvailableOrderRadii([]);
      const autoInner = innerSizes.length === 1 ? innerSizes[0] : '';
      const autoRadius = firstEntry?.radii && firstEntry.radii.length > 0 ? firstEntry.radii[0] : '';
      const autoName = generateMergedProductName({
        modelNumber: val,
        size: autoInner,
        radius: autoRadius,
        designType: firstEntry ? firstEntry.designType : 'OP',
        vs: firstEntry ? firstEntry.vs : '',
        hs: firstEntry ? firstEntry.hs : '',
        sr: firstEntry ? firstEntry.sr : ''
      });
      setOrderForm(prev => ({
        ...prev, modelNumber: val, productName: autoName, productSize: autoInner, radius: autoRadius,
        designType: firstEntry ? firstEntry.designType : 'OP', vs: firstEntry ? firstEntry.vs : '', hs: firstEntry ? firstEntry.hs : '', sr: firstEntry ? firstEntry.sr : ''
      }));
    }
  };

  const handleOrderInnerSizeChange = (e) => {
    const selectedInner = e.target.value;
    const match = productTemplates.find(item => item.modelNumber === orderForm.modelNumber && (item.innerSize === selectedInner || item.size === selectedInner));
    if (match) {
      setAvailableOrderRadii(match.radii || []);
      const selectedRadius = match.radii && match.radii.length > 0 ? match.radii[0] : '';
      const mergedName = generateMergedProductName({
        modelNumber: orderForm.modelNumber,
        size: selectedInner,
        radius: selectedRadius,
        designType: match.designType || orderForm.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      });
      setOrderForm(prev => ({
        ...prev,
        productName: mergedName,
        productSize: selectedInner,
        description: match.outerSize ? `Outer: ${match.outerSize}` : prev.description,
        radius: selectedRadius,
        designType: match.designType || prev.designType,
        vs: match.vs || 'N/A',
        hs: match.hs || 'N/A',
        sr: match.sr || 'N/A'
      }));
    } else {
      setOrderForm(prev => ({ ...prev, productSize: selectedInner }));
    }
  };

  const handleDeleteItem = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await operationsAPI.deleteInventoryItem(id);
      toast.success('Item deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  // Create Build Order
  const handlePlaceBuildOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const finalForm = {
        ...orderForm,
        productName: orderForm.productName || (orderForm.modelNumber ? `Drag Chain ${orderForm.modelNumber}` : 'Drag Chain')
      };
      await operationsAPI.createBuildJob(finalForm);
      toast.success('Production order placed! Supervisor has been alerted in real-time.');
      setOrderForm({
        productName: '',
        itemType: 'finished_good',
        salesPersonName: user?.name || '',
        chainLength: '1000',
        orderQuantity: 1,
        unitPrice: 0,
        unit: 'pcs',
        modelNumber: '',
        productSize: '',
        description: '',
        radius: 'N/A',
        designType: 'OP',
        vs: 'N/A',
        hs: 'N/A',
        sr: 'N/A'
      });
      setIsOrderCustomModel(false);
      setAvailableOrderInnerSizes([]);
      setAvailableOrderRadii([]);
      setActiveTab('place_orders');
      fetchData();
    } catch (err) {
      toast.error('Failed to place production order');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEditBuild = (job) => {
    setEditingBuildJob(job);
    setEditBuildForm({
      productName: job.productName || '',
      orderQuantity: job.orderQuantity || 1,
      status: job.status || 'pending',
      machineName: job.machineName || (job.machineId?.name || ''),
      estimatedDate: job.estimatedDate ? new Date(job.estimatedDate).toISOString().split('T')[0] : ''
    });
    setShowEditBuildModal(true);
  };

  const handleSaveEditBuild = async (e) => {
    e.preventDefault();
    if (!editingBuildJob) return;
    setSubmitting(true);
    try {
      await operationsAPI.updateBuildJob(editingBuildJob._id, editBuildForm);
      toast.success('Build Job updated successfully!');
      setShowEditBuildModal(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to update Build Job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBuildJob = async (jobId) => {
    if (window.confirm('Are you sure you want to delete this manufacturing build queue entry?')) {
      try {
        await operationsAPI.deleteBuildJob(jobId);
        toast.success('Manufacturing queue entry deleted successfully!');
        fetchData();
      } catch (err) {
        toast.error('Failed to delete manufacturing queue entry');
      }
    }
  };

  // Shortage / Buy / Sales Log CRUD (Sales Full Control)
  const handleSaveSbs = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingSbsId) {
        await operationsAPI.updateShortageBuySale(editingSbsId, sbsForm);
        toast.success('Communication log updated');
      } else {
        await operationsAPI.createShortageBuySale(sbsForm);
        toast.success('Communication log created & synced to team in real-time');
      }
      setShowSbsModal(false);
      setSbsForm({
        type: 'shortage',
        itemName: '',
        itemType: 'raw_material',
        quantity: 0,
        unitPrice: 0,
        unit: 'pcs',
        modelNumber: '',
        size: '',
        description: '',
        radius: 'N/A',
        designType: 'OP',
        vs: 'N/A',
        hs: 'N/A',
        sr: 'N/A',
        assignedTo: 'unassigned',
        remarks: ''
      });
      setIsSbsCustomModel(false);
      setAvailableSbsInnerSizes([]);
      setAvailableSbsRadii([]);
      setEditingSbsId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save log');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditSbs = (log) => {
    const matchesTemplate = productTemplates.some(t => t.modelNumber === log.modelNumber);
    setIsSbsCustomModel(!!log.modelNumber && !matchesTemplate);
    setSbsForm({
      type: log.type,
      itemName: log.itemName,
      itemType: log.itemType || 'raw_material',
      quantity: log.quantity,
      unitPrice: log.unitPrice || 0,
      unit: log.unit || 'pcs',
      modelNumber: log.modelNumber || '',
      size: log.size || '',
      description: log.description || '',
      radius: log.radius || 'N/A',
      designType: log.designType || 'OP',
      vs: log.vs || 'N/A',
      hs: log.hs || 'N/A',
      sr: log.sr || 'N/A',
      assignedTo: log.assignedTo || 'unassigned',
      remarks: log.remarks || ''
    });
    setEditingSbsId(log._id);
    setShowSbsModal(true);
  };

  const handleDeleteSbs = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await operationsAPI.deleteShortageBuySale(id);
      toast.success('Log entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete log entry');
    }
  };




  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, buildRes, qcRes, sbsRes, shortRes, tempRes] = await Promise.all([
        operationsAPI.getInventory(),
        operationsAPI.getBuildJobs(),
        operationsAPI.getQualityLogs(),
        operationsAPI.getShortageBuySales(),
        orderAPI.getShortages(),
        operationsAPI.getProductTemplates()
      ]);
      setInventory(invRes.data.data);
      setBuildJobs(buildRes.data.data);
      setQualityLogs(qcRes.data.data);
      setSbsLogs(sbsRes.data.data);
      setOrderShortages(shortRes.data.data);
      setProductTemplates(tempRes.data.data);
    } catch (err) {
      toast.error('Failed to load sales database');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    let socket;
    if (user?.organizationId) {
      socket = io(SOCKET_URL, { withCredentials: true });
      socket.emit('join_org', user.organizationId);

      socket.on('inventory_updated', fetchData);
      socket.on('qc_updated', fetchData);
      socket.on('product_sent_to_store', fetchData);
      socket.on('build_updated', fetchData);
      socket.on('quality_updated', fetchData);
      socket.on('communication_updated', fetchData);
      socket.on('product_templates_updated', fetchData);
    }

    return () => {
      if (socket) socket.disconnect();
    };
  }, [fetchData, user?.organizationId]);

  // QC Log CRUD (Sales full access)
  const handleSaveQcLog = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('materialName', qcForm.materialName);
      formData.append('quantity', qcForm.quantity);
      formData.append('unit', qcForm.unit);
      formData.append('remarks', qcForm.remarks);
      formData.append('qcType', qcForm.qcType);
      formData.append('invoiceNumber', qcForm.invoiceNumber);

      if (invoiceFile) {
        formData.append('invoiceFile', invoiceFile);
      } else {
        formData.append('invoiceUrl', qcForm.invoiceUrl);
      }

      if (editingQcId) {
        await operationsAPI.updateQualityLog(editingQcId, formData);
        toast.success('Quality invoice record updated');
      } else {
        await operationsAPI.createQualityLog(formData);
        toast.success('Quality invoice record created');
      }
      setShowQcModal(false);
      setQcForm({ materialName: '', quantity: 0, unit: 'pcs', remarks: '', qcType: 'inspected', invoiceNumber: '', invoiceUrl: '' });
      setInvoiceFile(null);
      setEditingQcId(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to save quality log');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditQcLog = (log) => {
    setQcForm({
      materialName: log.materialName,
      quantity: log.quantity,
      unit: log.unit,
      remarks: log.remarks || '',
      qcType: log.qcType,
      invoiceNumber: log.invoiceNumber || '',
      invoiceUrl: log.invoiceUrl || ''
    });
    setInvoiceFile(null);
    setEditingQcId(log._id);
    setShowQcModal(true);
  };

  const handleDeleteQcLog = async (id) => {
    if (!window.confirm('Delete this quality log/invoice entry?')) return;
    try {
      await operationsAPI.deleteQualityLog(id);
      toast.success('Quality log entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete log');
    }
  };

  const handleVerifyQC = async (id) => {
    try {
      await operationsAPI.verifyQualityLog(id);
      toast.success('Quality document verified!');
      fetchData();
    } catch (err) {
      toast.error('Failed to verify document');
    }
  };

  // Sourcing Shortage/Buy Order Action
  const handleOrderShortage = async (job) => {
    try {
      await operationsAPI.updateShortageBuySale(job._id, { status: 'ordered' });
      if (job.type === 'sale') {
        toast.success('Sale registered successfully!');
      } else {
        toast.success('Shortage raw materials ordered! Logged in pipeline.');
      }
      fetchData();
    } catch (err) {
      toast.error(job.type === 'sale' ? 'Failed to register sale' : 'Failed to order shortage items');
    }
  };

  const handleAddSourcingRemarks = async (id, remarks) => {
    try {
      await operationsAPI.updateShortageBuySale(id, { remarks });
      toast.success('Remarks updated in real-time');
      fetchData();
    } catch (err) {
      toast.error('Failed to save remarks');
    }
  };

  const getRequiredQty = (item) => {
    let required = 0;
    const itemIdStr = item._id ? item._id.toString() : '';
    const itemNameLower = (item.name || '').toLowerCase().trim();
    const itemModelLower = (item.modelNumber || '').toLowerCase().trim();

    // 1. Calculate from active buildJobs (Order Product Build)
    buildJobs.forEach(job => {
      if (['pending', 'processing', 'shortage_reported', 'assigned'].includes(job.status)) {
        const jobInvId = job.inventoryItemId ? (job.inventoryItemId._id || job.inventoryItemId).toString() : '';
        const matchesId = itemIdStr && jobInvId && itemIdStr === jobInvId;
        const jobNameLower = (job.productName || '').toLowerCase().trim();
        const jobModelLower = (job.modelNumber || '').toLowerCase().trim();
        
        const matchesModel = itemModelLower && (
          (jobModelLower && jobModelLower === itemModelLower) ||
          (jobNameLower && jobNameLower.includes(itemModelLower))
        );
        const matchesName = itemNameLower && jobNameLower && (jobNameLower.includes(itemNameLower) || itemNameLower.includes(jobNameLower));

        if (matchesId || matchesModel || matchesName) {
          required += Number(job.orderQuantity) || 0;
        }
      }
    });

    // 2. Calculate from sbsLogs (ShortageBuySale)
    sbsLogs.forEach(log => {
      // Prevent double-counting: skip shortage logs that are linked to active BuildJobs (since BuildJob orderQuantity is already counted)
      const isLinkedToActiveBuildJob = buildJobs.some(job => {
        if (!['pending', 'processing', 'shortage_reported', 'assigned'].includes(job.status)) return false;
        const jobShortageId = job.shortageLogId ? (job.shortageLogId._id || job.shortageLogId).toString() : '';
        const logIdStr = log._id ? log._id.toString() : '';
        return jobShortageId && logIdStr && jobShortageId === logIdStr;
      });

      if (!isLinkedToActiveBuildJob && ['shortage', 'buy'].includes(log.type) && ['pending', 'assigned', 'ordered'].includes(log.status)) {
        const logInvId = log.inventoryItemId ? (log.inventoryItemId._id || log.inventoryItemId).toString() : '';
        const matchesId = itemIdStr && logInvId && itemIdStr === logInvId;
        const logItemLower = (log.itemName || '').toLowerCase().trim();
        const logModelLower = (log.modelNumber || '').toLowerCase().trim();
        
        const matchesModel = itemModelLower && logModelLower && itemModelLower === logModelLower;
        const matchesName = logItemLower && itemNameLower && (logItemLower.includes(itemNameLower) || itemNameLower.includes(logItemLower));
        
        if (matchesId || matchesModel || matchesName) {
          required += Number(log.quantity) || 0;
        }
      }
    });

    // 3. Calculate from orderShortages (ShortageReport)
    orderShortages.forEach(s => {
      if (['pending_planning', 'pending'].includes(s.status) && s.modelNumber) {
        const sModelLower = s.modelNumber.toLowerCase().trim();
        const matchesName = itemNameLower && (sModelLower.includes(itemNameLower) || itemNameLower.includes(sModelLower));
        const matchesModel = itemModelLower && (sModelLower.includes(itemModelLower) || itemModelLower.includes(sModelLower));
        if (matchesName || matchesModel) {
          required += Number(s.shortageMeters) || 0;
        }
      }
    });

    return required;
  };

  const filteredInventory = useMemo(() => {
    return (inventory || [])
      .filter(item =>
        item.qualityStatus !== 'rejected' && (
          (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
          (item.modelNumber || '').toLowerCase().includes(search.toLowerCase()) ||
          (item.type || '').toLowerCase().includes(search.toLowerCase()) ||
          (item.rack || '').toLowerCase().includes(search.toLowerCase())
        )
      )
      .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  }, [inventory, search]);

  const uniqueRadii = Array.from(new Set(productTemplates.map(t => t.radius).filter(Boolean))).filter(x => x !== 'N/A').sort();


  return (
    <DashboardLayout pageTitle="Sales & Sourcing Workspace">
      
      {/* Tabs */}
      <div className="tab-row">
        <TabBar>
        <TabBtn active={activeTab === 'sales_pipeline'} onClick={() => setActiveTab('sales_pipeline')} icon={<HiOutlineUserGroup />}>
          Sales Pipeline & Funnel
        </TabBtn>
        <TabBtn active={activeTab === 'store_stock'} onClick={() => setActiveTab('store_stock')} icon={<HiOutlineCube />}>
          Available Store Stock
        </TabBtn>

        <TabBtn active={activeTab === 'shortages_buy'} onClick={() => setActiveTab('shortages_buy')} icon={<HiOutlineClipboardList />}>
          Shortage & Buy Logs
        </TabBtn>
        <TabBtn active={activeTab === 'place_orders'} onClick={() => setActiveTab('place_orders')} icon={<HiOutlineShoppingBag />}>
          Manufacturing Orders
        </TabBtn>
        <TabBtn active={activeTab === 'qc_verification'} onClick={() => setActiveTab('qc_verification')} icon={<HiOutlineClipboardCheck />}>
          QC Invoice Verification ({qualityLogs.filter(l => l.invoiceNumber && l.status === 'pending_verification').length})
        </TabBtn>
        <TabBtn active={activeTab === 'invoice_generator'} onClick={() => setActiveTab('invoice_generator')} icon={<HiOutlineReceiptTax />}>
          Bill & Invoice Generator
        </TabBtn>
        <TabBtn active={activeTab === 'dispatch'} onClick={() => setActiveTab('dispatch')} icon={<HiOutlineTruck />}>
          Dispatch & Shipping Logistics
        </TabBtn>
        </TabBar>
        <div className="tab-row-actions">
          <button className="btn btn-secondary btn-sm" onClick={fetchData}><HiOutlineRefresh /> Reload</button>
        </div>
      </div>



      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
          <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading Sales operations...
        </div>
      ) : activeTab === 'sales_pipeline' ? (
        <SalesPipelineTab />
      ) : activeTab === 'invoice_generator' ? (
        <InvoiceGenerator isEmbedded={true} />
      ) : activeTab === 'store_stock' ? (



        
        /* STORE STOCK VIEW */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>Available Inventory Levels</h3>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div className="form-input-icon" style={{ width: '260px' }}>
                <HiOutlineSearch className="icon" />
                <input type="text" className="form-input" style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }} placeholder="Search available items or model..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="azure-table-container">
            <table className="azure-table">
              <thead>
                <tr>
                  <th>Item Name</th>
                  <th>Model Number</th>
                  <th>Type</th>
                  <th style={{ color: '#10b981', fontWeight: 800 }}>In-Stock (Physical)</th>
                  <th>Available Qty (Net)</th>
                  <th>Required Qty</th>
                  <th>Maintained Qty (Min Stock)</th>
                  <th>Rack</th>

                  <th>Item Specs</th>
                  <th>Last Updated</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {filteredInventory.length === 0 ? <tr><td colSpan={12} style={{ textAlign: 'center', padding: '20px' }}>No items in stock.</td></tr> : filteredInventory.map(item => {
                  const reqQty = getRequiredQty(item);
                  const isRecentlyUpdated = item.updatedAt && (new Date() - new Date(item.updatedAt)) < (24 * 60 * 60 * 1000);
                  const isQcVerified = item.qualityStatus === 'verified';
                  return (
                    <tr key={item._id} style={isRecentlyUpdated && isQcVerified ? { background: 'rgba(16, 185, 129, 0.05)' } : {}}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <span>{item.name}</span>
                          {(isRecentlyUpdated && isQcVerified) && (
                            <span className="azure-badge success" style={{ fontSize: '0.68rem', padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                              ✨ QC Passed
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item.modelNumber || 'N/A'}</td>
                      <td style={{ textTransform: 'uppercase', fontSize: '0.8rem' }}>{item.type.replace('_', ' ')}</td>
                      <td style={{ fontWeight: 800, fontSize: '0.95rem', color: '#10b981' }}>
                        {item.quantity} {item.unit}
                      </td>
                      <td style={{ fontWeight: 700, color: (item.quantity - reqQty) <= 0 ? 'var(--danger)' : '#38bdf8' }}>
                        {Math.max(0, item.quantity - reqQty)} {item.unit}
                      </td>
                      <td style={{ fontWeight: 700, color: reqQty > 0 ? '#ef4444' : 'var(--text-muted)' }}>
                        {reqQty} {item.unit}
                      </td>
                      <td style={{ fontWeight: 700, color: '#f59e0b' }}>
                        {item.minStock || item.maintainedQuantity || 0} {item.unit}
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{item.rack || 'N/A'}</td>
                      <td style={{ fontSize: '0.85rem' }}>{item.size || 'N/A'}</td>
                      <td style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                        {item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : 'N/A')}
                      </td>

                      {/* Status Column (NON-EDITABLE FOR SALES) */}
                      <td>
                        {(!item.productionStatus || item.productionStatus === 'none') ? (
                          <span className="azure-badge warning" style={{ opacity: 0.7, whiteSpace: 'nowrap' }}>Not Sent</span>
                        ) : item.productionStatus === 'sent_to_pm' ? (
                          <span className="azure-badge warning" style={{ whiteSpace: 'nowrap' }}>Sent to PM</span>
                        ) : item.productionStatus === 'assigned_to_supervisor' ? (
                          <span className="azure-badge running" style={{ whiteSpace: 'nowrap' }}>Assigned: {item.assignedSupervisor}</span>
                        ) : (
                          <span className="azure-badge success" style={{ whiteSpace: 'nowrap' }}>Completed</span>
                        )}
                      </td>

                      {/* Remark Column (NON-EDITABLE FOR SALES) */}
                      <td>
                        {item.storeRemarks || item.productionStatus !== 'none' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#22c55e', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(34, 197, 94, 0.15)', border: '1px solid #22c55e' }}>
                              <HiOutlineCheckCircle style={{ fontSize: '0.9rem', color: '#22c55e' }} />
                            </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{item.storeRemarks || 'Sent to PM'}</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#ef4444', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444' }}>
                              <HiOutlineX style={{ fontSize: '0.8rem', color: '#ef4444' }} />
                            </span>
                            <span style={{ color: 'var(--text-dim)' }}>No Remark</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      ) : activeTab === 'shortages_buy' ? (

        /* SHORTAGES BUY LOGS */
        <div>
          <div className="azure-card" style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>Active Purchase, Sales & Shortages Log</h3>

            </div>
            {sbsLogs.filter(j => ['shortage', 'buy', 'sale'].includes(j.type)).length === 0 ? <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No active shortages, sales, or buy requests logged.</p> : (
              <div className="azure-table-container" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                <table className="azure-table" style={{ width: '100%', minWidth: '1500px', whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr>
                      <th>Item Name</th>
                      <th>Log Type</th>
                      <th>Item Type</th>
                      <th>Model Number</th>
                      <th>Inner Size</th>
                      <th>Outer Size</th>
                      {isDragChain && (
                        <>
                          <th>Bending Radius</th>
                          <th>VS</th>
                          <th>HS</th>
                          <th>SR</th>
                        </>
                      )}
                      <th>Required Qty</th>
                      <th>Assignee</th>
                      <th>Sourcing Status</th>
                      <th>Purchase Remarks</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sbsLogs.filter(j => ['shortage', 'buy', 'sale'].includes(j.type)).map(job => (
                      <tr key={job._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                          {job.itemName}
                          {(() => {
                            const linkedJob = buildJobs.find(j => j.shortageLogId === job._id);
                            if (linkedJob) {
                              return (
                                <div style={{ fontSize: '0.75rem', color: 'var(--primary)', marginTop: '4px', padding: '2px 6px', background: 'rgba(249, 115, 22, 0.05)', border: '1px solid rgba(249, 115, 22, 0.1)', borderRadius: '4px', display: 'inline-block', fontWeight: 'normal' }}>
                                  🔨 <strong>Building Status:</strong> <span style={{ textTransform: 'capitalize' }}>{linkedJob.status}</span> 
                                  {linkedJob.estimatedDate && ` | Est: ${new Date(linkedJob.estimatedDate).toLocaleString()}`}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </td>
                        <td>
                          <span className={`azure-badge ${job.type === 'shortage' ? 'danger' : job.type === 'buy' ? 'warning' : 'success'}`}>
                            {job.type}
                          </span>
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>{job.itemType || 'raw_material'}</td>
                        <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{job.modelNumber || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{job.size || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{job.description?.replace('Outer: ', '') || 'N/A'}</td>
                        {isDragChain && (
                          <>
                            <td style={{ fontSize: '0.85rem' }}>{job.radius || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.vs || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.hs || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.sr || 'N/A'}</td>
                          </>
                        )}
                        <td style={{ fontWeight: 700 }}>{job.quantity} {job.unit}</td>
                        <td style={{ fontWeight: 600, color: '#3b82f6' }}>{job.assignedTo}</td>
                        <td>
                          <span className={`azure-badge ${
                            job.status === 'pending' ? 'warning' :
                            job.status === 'assigned' ? 'running' : 'success'
                          }`}>
                            {job.status}
                          </span>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-input"
                            style={{ padding: '6px 12px', fontSize: '0.85rem', width: '220px' }}
                            placeholder="Add sourcing updates..."
                            defaultValue={job.remarks || ''}
                            onBlur={(e) => handleAddSourcingRemarks(job._id, e.target.value)}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            {!['ordered', 'completed', 'received'].includes(job.status) && ['sales', 'store_manager', 'unassigned', ''].includes(job.assignedTo) ? (
                              <button className="btn btn-primary btn-sm" onClick={() => handleOrderShortage(job)}>
                                {job.type === 'sale' ? 'Sale' : 'Order'}
                              </button>
                            ) : ['ordered', 'completed', 'received'].includes(job.status) ? (
                              <span style={{ color: 'var(--success)', fontSize: '0.82rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                ✓ {job.status === 'completed' ? 'Completed' : job.type === 'sale' ? 'Sold' : 'Ordered'}
                              </span>
                            ) : null}
                            <button style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleEditSbs(job)}>
                              <HiOutlinePencil /> Edit
                            </button>
                            <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleDeleteSbs(job._id)}>
                              <HiOutlineTrash /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Client Order Shortages (Difference Reports) */}
          <div className="azure-card" style={{ padding: '24px', marginTop: '28px' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
              ⚠️ Client Order Shortages (Difference Reports)
            </h3>
            {orderShortages.length === 0 ? (
              <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '20px' }}>No active client order shortages.</p>
            ) : (
              <div className="azure-table-container">
                <table className="azure-table">
                  <thead>
                    <tr>
                      <th>Ref Order</th>
                      <th>Model Number</th>
                      <th>Shortage Qty</th>
                      <th>Status</th>
                      <th>Planned Shots</th>
                      <th>Date Flagged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderShortages.map(s => (
                      <tr key={s._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{s.orderId?.orderNumber || 'N/A'}</td>
                        <td style={{ fontWeight: 600 }}>{s.modelNumber}</td>
                        <td style={{ fontWeight: 700, color: 'var(--danger)' }}>{s.shortageMeters}m</td>
                        <td>
                          <span className={`azure-badge ${s.status === 'pending_planning' ? 'danger' : 'success'}`}>
                            {s.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td>{s.plannedShots > 0 ? `${s.plannedShots} shots` : 'Not Planned'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{new Date(s.createdAt).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      ) : activeTab === 'place_orders' ? (

        /* MANUFACTURING ORDERS VIEW */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '28px' }}>
          {/* Order Placement Form */}
          <div className="azure-card" style={{ padding: '24px', height: 'fit-content' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.1rem' }}>
              <HiOutlinePlus style={{ color: 'var(--primary)' }} /> Order Product Build
            </h3>
            <form onSubmit={handlePlaceBuildOrder}>
              {/* 1. Model Number */}
              {isDragChain ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Model Number *</label>
                    <select
                      className="form-input"
                      required
                      value={isOrderCustomModel ? 'custom' : orderForm.modelNumber}
                      onChange={handleOrderModelChange}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">-- Select Model Number --</option>
                      {catalogModelNumbers.map(m => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                      <option value="custom">-- Custom Model Number / Manual Entry --</option>
                    </select>
                  </div>

                  {isOrderCustomModel && (
                    <div className="form-group">
                      <label className="form-label">Custom Model Number *</label>
                      <input
                        type="text"
                        className="form-input"
                        required
                        value={orderForm.modelNumber}
                        onChange={e => setOrderForm({ ...orderForm, modelNumber: e.target.value, productName: `Drag Chain ${e.target.value}` })}
                        placeholder="e.g. STAR-CUSTOM-100"
                      />
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label className="form-label">Model Number *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={orderForm.modelNumber}
                      onChange={e => setOrderForm({ ...orderForm, modelNumber: e.target.value })}
                      placeholder="e.g. Model-123"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      required
                      value={orderForm.productName || ''}
                      onChange={e => setOrderForm({ ...orderForm, productName: e.target.value })}
                      placeholder="e.g. Nylon Slices"
                    />
                  </div>
                </>
              )}

              {/* Sales Person Name */}
              <div className="form-group" style={{ marginBottom: '14px' }}>
                <label className="form-label">Sales Person Name *</label>
                <input
                  type="text"
                  className="form-input"
                  required
                  value={orderForm.salesPersonName || user?.name || ''}
                  onChange={e => setOrderForm({ ...orderForm, salesPersonName: e.target.value })}
                  placeholder="e.g. Rahul Sharma (Sales Officer)"
                />
              </div>

              {/* 2. Item Type + Stock Unit */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Item Type *</label>
                  <select className="form-input" value={orderForm.itemType || 'finished_good'} onChange={e => setOrderForm({ ...orderForm, itemType: e.target.value })}>
                    <option value="finished_good">Finished Good</option>
                    <option value="raw_material">Raw Material</option>
                    <option value="hardware">Hardware / Tools</option>
                    <option value="custom">Custom Tooling</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Stock Unit *</label>
                  <select className="form-input" required value={orderForm.unit || 'pcs'} onChange={e => setOrderForm({ ...orderForm, unit: e.target.value })} style={{ cursor: 'pointer' }}>
                    <option value="pcs">pcs (Pieces)</option>
                    <option value="pkt">pkt (Packets)</option>
                    <option value="kg">kg (Kilograms)</option>
                    <option value="meters">meters (Meters)</option>
                    <option value="boxes">boxes (Boxes)</option>
                    <option value="liters">liters (Liters)</option>
                    <option value="bags">bags (Bags)</option>
                    <option value="rolls">rolls (Rolls)</option>
                  </select>
                </div>
              </div>

              {/* 4. Cascading Spec Section (Inner Size, Outer Size, Bending Radius, Design Type, VS, HS, SR) */}
              {isDragChain && (
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-light)', marginBottom: '16px' }}>

                {/* Row 1: Inner Size + Outer Size */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Inner Size</label>
                    {availableOrderInnerSizes.length > 0 ? (
                      <select
                        className="form-input"
                        value={orderForm.productSize}
                        onChange={handleOrderInnerSizeChange}
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="">-- Choose Inner Size --</option>
                        {availableOrderInnerSizes.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        className="form-input"
                        value={orderForm.productSize}
                        onChange={e => setOrderForm({ ...orderForm, productSize: e.target.value })}
                        placeholder="e.g. 35X140 OP"
                      />
                    )}
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Outer Size (Auto-Filled)</label>
                    <input
                      type="text"
                      className="form-input"
                      readOnly
                      value={orderForm.description?.replace('Outer: ', '') || 'Auto-generated'}
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)' }}
                    />
                  </div>
                </div>

                {/* Row 2: Bending Radius (Only for Drag Chain) */}
                {isDragChain && (
                  <div style={{ marginBottom: '12px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: '0.8rem' }}>Bending Radius</label>
                      <select
                        className="form-input"
                        value={orderForm.radius}
                        onChange={e => setOrderForm({ ...orderForm, radius: e.target.value })}
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="">-- Select Radius --</option>
                        {availableOrderRadii.length > 0 ? (
                          availableOrderRadii.map(r => <option key={r} value={r}>{r}</option>)
                        ) : (
                          ['15', '25', '40', '50', '55', '75', '100', '125', '150', '175', '200', '225', '250', '300', '1000'].map(r => (
                            <option key={r} value={r}>{r}</option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                )}
                </div>
              )}

              {/* 5. Length in mm & Required Build Quantity (pcs) */}
              {isDragChain ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Length in mm / m *</label>
                      <input
                        type="number"
                        className="form-input"
                        required
                        min="0.001"
                        step="any"
                        value={orderForm.chainLength || ''}
                        onChange={e => setOrderForm({ ...orderForm, chainLength: e.target.value })}
                        placeholder="e.g. 1000 or 1.5"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Required Build Quantity ({orderForm.unit || 'pcs / m'}) *</label>
                      <input
                        type="number"
                        className="form-input"
                        required
                        min="0.001"
                        step="any"
                        value={orderForm.orderQuantity}
                        onChange={e => setOrderForm({ ...orderForm, orderQuantity: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                        placeholder="e.g. 5 or 2.5"
                      />
                    </div>
                  </div>
                  {orderForm.chainLength && orderForm.orderQuantity && (
                    <div style={{ padding: '8px 12px', background: 'rgba(59,130,246,0.08)', borderRadius: '8px', border: '1px solid rgba(59,130,246,0.2)', marginBottom: '16px', fontSize: '0.82rem', color: 'var(--primary)' }}>
                      📏 Total Production Length: <strong>{(Number(orderForm.chainLength) > 50 ? ((Number(orderForm.chainLength) * Number(orderForm.orderQuantity)) / 1000).toFixed(2) : (Number(orderForm.chainLength) * Number(orderForm.orderQuantity)).toFixed(2))} Meters</strong> ({orderForm.orderQuantity} × {orderForm.chainLength} {Number(orderForm.chainLength) > 50 ? 'mm' : 'm'})
                    </div>
                  )}
                </>
              ) : (
                <div className="form-group">
                  <label className="form-label">Required Build Quantity ({orderForm.unit || 'pcs'}) *</label>
                  <input type="number" className="form-input" required min="0.001" step="any" value={orderForm.orderQuantity} onChange={e => setOrderForm({ ...orderForm, orderQuantity: e.target.value === '' ? '' : parseFloat(e.target.value) })} />
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
                <HiOutlineSave /> {submitting ? 'Placing Order...' : 'Submit Build Order'}
              </button>
            </form>
          </div>

          {/* Active Orders List */}
          <div className="azure-card" style={{ padding: '24px' }}>
            <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', fontSize: '1.1rem' }}>Manufacturing Queue Status</h3>
            {buildJobs.length === 0 ? <p style={{ color: 'var(--text-dim)' }}>No orders in queue.</p> : (
              <div className="azure-table-container" style={{ overflowX: 'auto', width: '100%', WebkitOverflowScrolling: 'touch' }}>
                <table className="azure-table" style={{ width: '100%', minWidth: '1500px', whiteSpace: 'nowrap' }}>
                  <thead>
                    <tr>
                      <th>Product Name</th>
                      <th>Item Type</th>
                      <th>Model Number</th>
                      <th>Inner Size</th>
                      <th>Outer Size</th>
                      {isDragChain && (
                        <>
                          <th>Length (mm)</th>
                          <th>Bending Radius</th>
                          <th>VS</th>
                          <th>HS</th>
                          <th>SR</th>
                        </>
                      )}
                      <th>Required Quantity</th>
                      <th>Sales Person</th>
                      <th>Machine / Line</th>
                      <th>Materials Used</th>
                      <th>Process Stage & Est Date</th>
                      {['admin', 'super_admin'].includes(user?.role) && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {buildJobs.map(job => (
                      <tr key={job._id}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{job.productName}</td>
                        <td style={{ fontSize: '0.85rem' }}>{job.itemType || 'finished_good'}</td>
                        <td style={{ fontWeight: 600, fontSize: '0.85rem' }}>{job.modelNumber || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem' }}>{job.productSize || 'N/A'}</td>
                        <td style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{job.description?.replace('Outer: ', '') || 'N/A'}</td>
                        {isDragChain && (
                          <>
                            <td style={{ fontSize: '0.85rem', fontWeight: 600 }}>{job.chainLength ? `${job.chainLength} mm` : 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.radius || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.vs || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.hs || 'N/A'}</td>
                            <td style={{ fontSize: '0.85rem' }}>{job.sr || 'N/A'}</td>
                          </>
                        )}
                        <td style={{ fontWeight: 700 }}>{job.orderQuantity} {job.unit || 'pcs'}</td>
                        <td style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--primary)' }}>
                          👤 {job.salesPersonName || job.orderedBy?.name || 'Sales Team'}
                        </td>
                        <td style={{ fontSize: '0.85rem' }}>
                          {job.machineId && typeof job.machineId === 'object' ? (
                            <div style={{ color: 'var(--primary)', fontWeight: 600 }}>⚙️ {job.machineId.name} {job.machineId.capacity ? `(${job.machineId.capacity})` : ''}</div>
                          ) : job.machineName ? (
                            <div style={{ color: 'var(--primary)', fontWeight: 600 }}>⚙️ {job.machineName}</div>
                          ) : <span style={{ color: 'var(--text-dim)' }}>Unassigned</span>}
                          {formatBuildProcessSpecs(job) && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px' }}>{formatBuildProcessSpecs(job)}</div>
                          )}
                        </td>
                        <td style={{ fontSize: '0.8rem' }}>
                          {job.materialsUsed && job.materialsUsed.length > 0 ? (
                            <div>📦 {job.materialsUsed.map(m => `${m.quantity} ${m.unit} ${m.materialName}`).join(', ')}</div>
                          ) : <span style={{ color: 'var(--text-dim)' }}>None</span>}
                        </td>
                        <td>
                          <span className={`azure-badge ${
                            job.status === 'pending' ? 'warning' :
                            job.status === 'shortage_reported' ? 'danger' :
                            job.status === 'processing' ? 'running' :
                            job.status === 'delayed' ? 'danger' : 'success'
                          }`}>
                            {job.status === 'delayed' ? '⚠️ delayed' : job.status.replace('_', ' ')}
                          </span>
                          {job.estimatedDate && (
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '4px' }}>
                              Est: {new Date(job.estimatedDate).toLocaleString()}
                            </div>
                          )}
                        </td>
                        {['admin', 'super_admin'].includes(user?.role) && (
                          <td>
                            <div style={{ display: 'inline-flex', gap: '6px' }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#3b82f6', borderColor: '#3b82f6', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleOpenEditBuild(job)}
                                title="Edit Manufacturing Queue Entry (Admin Only)"
                              >
                                <HiOutlinePencil /> Edit
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ color: '#ef4444', borderColor: '#ef4444', padding: '4px 10px', fontSize: '0.78rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                onClick={() => handleDeleteBuildJob(job._id)}
                                title="Delete Manufacturing Queue Entry (Admin Only)"
                              >
                                <HiOutlineTrash /> Delete
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

      ) : activeTab === 'qc_verification' ? (

        /* QC INVOICE VERIFICATION PANEL (WITH FULL CRUD FOR SALES) */
        <div className="azure-card" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ color: 'var(--text-primary)', fontSize: '1.1rem', margin: 0 }}>QC Invoice Document Registry</h3>
            <button className="btn btn-primary btn-sm" onClick={() => {
              setEditingQcId(null);
              setQcForm({ materialName: '', quantity: 0, unit: 'pcs', remarks: '', qcType: 'inspected', invoiceNumber: '', invoiceUrl: '' });
              setShowQcModal(true);
            }}><HiOutlinePlus /> Add QC Invoice Log</button>
          </div>

          <div className="azure-table-container">
            <table className="azure-table">
              <thead>
                <tr>
                  <th>Item Billed</th>
                  <th>Qty Sourced</th>
                  <th>Invoice Number</th>
                  <th>Document Scan</th>
                  <th>Status</th>
                  <th>Verification Action</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {qualityLogs.filter(l => l.invoiceNumber).length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-dim)' }}>
                      No QC invoice logs registered.
                    </td>
                  </tr>
                ) : (
                  qualityLogs.filter(l => l.invoiceNumber).map(log => (
                    <tr key={log._id}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                        {log.materialName}
                        <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '2px' }}>{log.remarks}</div>
                      </td>
                      <td style={{ fontWeight: 700 }}>{log.quantity} {log.unit}</td>
                      <td style={{ fontWeight: 700, color: 'var(--success)' }}>{log.invoiceNumber}</td>
                      <td>
                        {log.invoiceUrl ? (
                          <a href={`${SOCKET_URL}/api/upload/view/${log.invoiceUrl.split('/').pop()}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'var(--primary-light)', fontSize: '0.8rem' }}>
                            View Scan
                          </a>
                        ) : (
                          <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>No Scan</span>
                        )}
                      </td>
                      <td>
                        <span className={`azure-badge ${
                          log.status === 'pending_verification' ? 'warning' : 'success'
                        }`}>
                          {log.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        {log.status === 'pending_verification' ? (
                          <button className="btn btn-accent btn-sm" onClick={() => handleVerifyQC(log._id)}>
                            <HiOutlineCheckCircle /> Verify
                          </button>
                        ) : (
                          <span style={{ color: 'var(--success)', fontWeight: 600 }}>Verified</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <button style={{ color: '#3b82f6', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleEditQcLog(log)}>
                            <HiOutlinePencil /> Edit
                          </button>
                          <button style={{ color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '4px' }} onClick={() => handleDeleteQcLog(log._id)}>
                            <HiOutlineTrash /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      ) : activeTab === 'dispatch' ? (
        <DispatchTab />
      ) : null}

      {/* QC LOG ENTRY MODAL (Sales CRUD) */}
      {showQcModal && (
        <ModalOverlay onClose={() => setShowQcModal(false)} title={editingQcId ? "Edit Invoice Log" : "Log New Sourcing Invoice"}>
          <form onSubmit={handleSaveQcLog}>
            <div className="form-group">
              <label className="form-label">Material / Item Billed *</label>
              <input type="text" className="form-input" required value={qcForm.materialName} onChange={e => setQcForm({ ...qcForm, materialName: e.target.value })} placeholder="e.g. STR-35 Drag Chain links" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Quantity *</label>
                <input type="number" step="0.01" className="form-input" required value={qcForm.quantity} onChange={e => setQcForm({ ...qcForm, quantity: Number(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Unit *</label>
                <input type="text" className="form-input" required value={qcForm.unit} onChange={e => setQcForm({ ...qcForm, unit: e.target.value })} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.8fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Report Category *</label>
                <select className="form-input" value={qcForm.qcType} onChange={e => setQcForm({ ...qcForm, qcType: e.target.value })}>
                  <option value="inspected">Inspected Goods</option>
                  <option value="damaged">Damaged Materials</option>
                  <option value="missed">Missed/Short Items</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Invoice Number *</label>
                <input type="text" className="form-input" required value={qcForm.invoiceNumber} onChange={e => setQcForm({ ...qcForm, invoiceNumber: e.target.value })} placeholder="Invoice code" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Upload Invoice Document</label>
              <input 
                type="file" 
                className="form-input" 
                onChange={e => setInvoiceFile(e.target.files[0])} 
                accept="image/*,application/pdf"
                style={{ padding: '8px 12px' }}
              />
              {qcForm.invoiceUrl && !invoiceFile && (
                <div style={{ fontSize: '0.78rem', marginTop: '6px' }}>
                  {/* Current: <a href={qcForm.invoiceUrl.startsWith('/uploads/') ? `http://${window.location.hostname}:5000${qcForm.invoiceUrl}` : qcForm.invoiceUrl} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'var(--primary-light)' }}>View Existing Scan</a> */}
                  Current: <a href={qcForm.invoiceUrl ? `${SOCKET_URL}/api/upload/view/${qcForm.invoiceUrl.split('/').pop()}` : '#'} target="_blank" rel="noreferrer" style={{ textDecoration: 'underline', color: 'var(--primary-light)' }}>View Existing Scan</a>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Inspector Remarks</label>
              <textarea className="form-input" rows="2" value={qcForm.remarks} onChange={e => setQcForm({ ...qcForm, remarks: e.target.value })} placeholder="Invoice/Discrepancy state notes..."></textarea>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyItems: 'flex-end', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowQcModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Invoice Log'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}
      {/* STOCK ITEM MODAL */}
      {showItemModal && (
        <ModalOverlay onClose={() => setShowItemModal(false)} title={editingItemId ? "Edit Stock Item" : "Add Inventory Stock Item"}>
          <form onSubmit={handleSaveItem}>

            {/* 1. Item Name — always first */}
            <div className="form-group">
              <label className="form-label">Item / Product Name *</label>
              <input
                type="text"
                className="form-input"
                required
                value={itemForm.name}
                onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g. Nylon Drag Chain"
              />
            </div>

            {/* 2. Item Type + Stock Unit */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Item Type *</label>
                <select className="form-input" value={itemForm.type} onChange={handleTypeChange}>
                  <option value="raw_material">Raw Material</option>
                  <option value="finished_good">Finished Good</option>
                  <option value="hardware">Hardware / Tools</option>
                  <option value="custom">Custom Tooling</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Stock Unit *</label>
                <select className="form-input" required value={itemForm.unit} onChange={e => setItemForm({ ...itemForm, unit: e.target.value })} style={{ cursor: 'pointer' }}>
                  <option value="pcs">pcs (Pieces)</option>
                  <option value="pkt">pkt (Packets)</option>
                  <option value="kg">kg (Kilograms)</option>
                  <option value="meters">meters (Meters)</option>
                  <option value="boxes">boxes (Boxes)</option>
                  <option value="liters">liters (Liters)</option>
                  <option value="bags">bags (Bags)</option>
                  <option value="rolls">rolls (Rolls)</option>
                </select>
              </div>
            </div>

            {/* 3. DRAG CHAIN CONFIGURATION SECTION — DB-driven dropdowns */}
            {isDragChain ? (
              <div style={{ padding: '16px', background: 'rgba(249,115,22,0.03)', borderRadius: '10px', border: '1px solid rgba(249,115,22,0.2)', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '1.1rem' }}>⚙️</span>
                  <h3 style={{ fontSize: '0.9rem', fontWeight: 700, margin: 0, color: 'var(--primary)' }}>
                    Drag Chain Configuration (PDF Specification)
                  </h3>
                  {catalogModelNumbers.length === 0 && (
                    <span style={{ fontSize: '0.72rem', color: '#f59e0b', marginLeft: 'auto' }}>
                      ⚠️ No catalog data — add product templates first
                    </span>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>

                  {/* 1. Model Number — from DB */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Model No. *</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.modelNumber}
                      onChange={e => handleModelChange(e.target.value)}
                    >
                      <option value="">-- Select Model --</option>
                      {catalogModelNumbers.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>

                  {/* 2. Width (Inner Width) — cascades from Model */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Width (Inner)</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.width}
                      onChange={e => setItemForm(p => ({ ...p, width: e.target.value }))}
                    >
                      <option value="">-- Select Width --</option>
                      {availableWidths.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>

                  {/* 3. Bending Radius — 5, 10, 15, 20 ... up to 700 */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Bending Radius</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.radius?.replace(/^R/i, '') || ''}
                      onChange={e => setItemForm(p => ({ ...p, radius: e.target.value }))}
                    >
                      <option value="">-- Select Radius --</option>
                      {BENDING_RADII_OPTIONS.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>

                  {/* 4. Type: OP / CP / SEMI */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Type (Open/Closed)</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.chainType || 'OP'}
                      onChange={e => setItemForm(p => ({ ...p, chainType: e.target.value }))}
                    >
                      <option value="OP">OP — Open</option>
                      <option value="CP">CP — Closed</option>
                      <option value="SEMI">SEMI — Semi-Closed</option>
                    </select>
                  </div>

                  {/* 5. Number of Links per meter — from DB */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>No. of Links (per m)</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.numberOfLinks}
                      onChange={e => setItemForm(p => ({ ...p, numberOfLinks: e.target.value }))}
                    >
                      <option value="">-- Select Links --</option>
                      {availableLinks.length > 0
                        ? availableLinks.map(l => <option key={l} value={l}>{l}</option>)
                        : ['18', '20', '22', '24', '26', '28', '30'].map(l => <option key={l} value={l}>{l}</option>)
                      }
                    </select>
                  </div>

                  {/* 6. Length */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Length (mm / m)</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.length}
                      onChange={e => setItemForm(p => ({ ...p, length: e.target.value }))}
                    >
                      <option value="">-- Select Length --</option>
                      {['500 MM', '1000 MM', '1500 MM', '2000 MM', '2500 MM', '3000 MM', '4000 MM', '5000 MM'].map(l => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>

                  {/* 7. Mounting Bracket Type */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Mounting Bracket Type</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.mountingBracketType}
                      onChange={e => setItemForm(p => ({ ...p, mountingBracketType: e.target.value }))}
                    >
                      <option value="">-- Select Bracket --</option>
                      <option value="B-BOX">B-BOX Type ({formatMountingBracketCode('B-BOX', itemForm.modelNumber || '35.1')})</option>
                      <option value="L-ANGULAR">L-ANGULAR Type ({formatMountingBracketCode('L-ANGULAR', itemForm.modelNumber || '35.1')})</option>
                      <option value="LSR(P)">LSR (P) — Plastic Angular Strain Relief ({formatMountingBracketCode('LSR(P)', itemForm.modelNumber || '35.1')})</option>
                      <option value="LSR(M)">LSR (M) — Metal Angular Strain Relief ({formatMountingBracketCode('LSR(M)', itemForm.modelNumber || '35.1')})</option>
                      <option value="SRP-25">SRP-25</option>
                      <option value="SRP-35">SRP-35</option>
                      <option value="SRP-50">SRP-50</option>
                    </select>
                  </div>

                  {/* 8. Accessories / Separator */}
                  <div className="form-group">
                    <label className="form-label" style={{ fontSize: '0.78rem' }}>Accessories (Separator)</label>
                    <select
                      className="form-input"
                      style={{ fontSize: '0.8rem', cursor: 'pointer' }}
                      value={itemForm.accessories}
                      onChange={e => setItemForm(p => ({ ...p, accessories: e.target.value }))}
                    >
                      <option value="">-- None / Not Required --</option>
                      <option value="HS">HS — Horizontal Separator</option>
                      <option value="VS">VS — Vertical Separator</option>
                      <option value="HS+VS">HS + VS (Both)</option>
                    </select>
                  </div>

                </div>

                {/* Live Spec Preview */}
                {itemForm.name && (
                  <div style={{ marginTop: '12px', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', fontSize: '0.8rem', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span>📋 Generated Product Name:</span>
                    <strong style={{ color: 'var(--primary)' }}>{itemForm.name}</strong>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label className="form-label">Model Number *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={itemForm.modelNumber}
                    onChange={e => setItemForm({ ...itemForm, modelNumber: e.target.value })}
                    placeholder="e.g. Model-123"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    required
                    value={itemForm.name || ''}
                    onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                    placeholder="e.g. Nylon Slices"
                  />
                </div>
              </>
            )}


            {/* 6. Maintained Qty (Min Stock) */}
            <div className="form-group" style={{ marginBottom: '16px' }}>
              <label className="form-label" style={{ color: '#f59e0b', fontWeight: 600 }}>Maintained Qty (Min Stock) ({itemForm.unit || 'meters'}) *</label>
              <input
                type="number"
                className="form-input"
                min={0}
                required
                value={itemForm.minStock ?? itemForm.maintainedQuantity ?? 0}
                onChange={e => setItemForm({
                  ...itemForm,
                  minStock: Math.max(0, Number(e.target.value) || 0),
                  maintainedQuantity: Math.max(0, Number(e.target.value) || 0)
                })}
                placeholder="e.g. 100"
              />
            </div>


            {/* 7. Location Hierarchy: Warehouse Location (Full Width) -> Almirah & Rack/Shelf (Grid Below) */}
            <div className="form-group">
              <label className="form-label">Warehouse Location</label>
              <input
                type="text"
                className="form-input"
                value={itemForm.warehouseLocation || ''}
                onChange={e => setItemForm({ ...itemForm, warehouseLocation: e.target.value })}
                placeholder="e.g. Main Godown / WH-1"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Almirah / Cupboard</span>
                  {excelAlmirahFound && (
                    <span style={{ fontSize: '0.72rem', color: '#3b82f6', fontWeight: 600 }}>
                      ⚡ Excel: {excelAlmirahFound}
                    </span>
                  )}
                </label>
                {autoAlmirahOptions.length > 0 ? (
                  <select
                    className="form-input"
                    value={itemForm.almirah || ''}
                    onChange={e => {
                      const selectedAlm = e.target.value;
                      const { rackOptions } = getSmartRackAndAlmirahInfo(itemForm.modelNumber, itemForm.size, itemForm.radius, selectedAlm);
                      setAutoRackOptions(rackOptions);
                      setItemForm(prev => ({
                        ...prev,
                        almirah: selectedAlm,
                        rack: rackOptions.length > 0 ? rackOptions[0] : prev.rack
                      }));
                    }}
                    style={{ cursor: 'pointer', border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.06)' }}
                  >
                    {autoAlmirahOptions.map(alm => (
                      <option key={alm} value={alm}>{alm}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={itemForm.almirah || ''}
                    onChange={e => setItemForm({ ...itemForm, almirah: e.target.value })}
                    placeholder="e.g. ALMIRAH NO 3"
                    style={excelAlmirahFound ? { border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.06)' } : {}}
                  />
                )}
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label className="form-label" style={{ fontSize: '0.82rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Rack / Shelf (S.No)</span>
                  {autoRackOptions.length > 0 && (
                    <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 600 }}>
                      ⚡ {autoRackOptions.length} Excel Racks
                    </span>
                  )}
                </label>
                {autoRackOptions.length > 0 ? (
                  <select
                    className="form-input"
                    value={itemForm.rack || ''}
                    onChange={e => setItemForm({ ...itemForm, rack: e.target.value })}
                    style={{ cursor: 'pointer', border: '1px solid #10b981', background: 'rgba(16,185,129,0.06)' }}
                  >
                    <option value="">-- Choose Rack (S.No) --</option>
                    {autoRackOptions.map(r => (
                      <option key={r} value={r}>S.No / Rack #{r}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="form-input"
                    value={itemForm.rack || ''}
                    onChange={e => setItemForm({ ...itemForm, rack: e.target.value })}
                    placeholder="e.g. Shelf 4A / S.No 10"
                  />
                )}
              </div>
            </div>

            {/* 8. Description */}
            <div className="form-group">
              <label className="form-label">Item Description</label>
              <textarea className="form-input" rows="2" value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })} placeholder="Supplier info..."></textarea>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowItemModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Item'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* SHORTAGE / BUY / SALE LOG MODAL (UNIFIED INVENTORY FLOW WITHOUT LOCATION) */}
      {showSbsModal && (
        <ModalOverlay onClose={() => setShowSbsModal(false)} title={editingSbsId ? "Edit Log Entry" : "Log Shortage, Buy or Sales Entry"}>
          <form onSubmit={handleSaveSbs}>
            {/* 1. Register Type */}
            <div className="form-group">
              <label className="form-label">Register Type *</label>
              <select className="form-input" value={sbsForm.type} onChange={e => setSbsForm({ ...sbsForm, type: e.target.value })}>
                <option value="shortage">Shortage (Lack of stock)</option>
                <option value="buy">Buy (Purchase Request)</option>
                <option value="sale">Sale (Sales Order)</option>
              </select>
            </div>

            {/* 2. Product Name */}
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input
                type="text"
                className="form-input"
                required
                value={sbsForm.itemName}
                onChange={e => setSbsForm({ ...sbsForm, itemName: e.target.value })}
                placeholder="e.g. Nylon Drag Chain"
              />
            </div>

            {/* 3. Item Type + Stock Unit */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Item Type *</label>
                <select className="form-input" value={sbsForm.itemType || 'raw_material'} onChange={e => setSbsForm({ ...sbsForm, itemType: e.target.value })}>
                  <option value="raw_material">Raw Material</option>
                  <option value="finished_good">Finished Good</option>
                  <option value="hardware">Hardware / Tools</option>
                  <option value="custom">Custom Tooling</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Stock Unit *</label>
                <select className="form-input" required value={sbsForm.unit || 'pcs'} onChange={e => setSbsForm({ ...sbsForm, unit: e.target.value })} style={{ cursor: 'pointer' }}>
                  <option value="pcs">pcs (Pieces)</option>
                  <option value="pkt">pkt (Packets)</option>
                  <option value="kg">kg (Kilograms)</option>
                  <option value="meters">meters (Meters)</option>
                  <option value="boxes">boxes (Boxes)</option>
                  <option value="liters">liters (Liters)</option>
                  <option value="bags">bags (Bags)</option>
                  <option value="rolls">rolls (Rolls)</option>
                </select>
              </div>
            </div>

            {/* 4. Model Number */}
            {isDragChain ? (
              <>
                <div className="form-group">
                  <label className="form-label">Model Number</label>
                  <select
                    className="form-input"
                    value={isSbsCustomModel ? 'custom' : sbsForm.modelNumber}
                    onChange={handleSbsModelChange}
                    style={{ cursor: 'pointer' }}
                  >
                    <option value="">-- Select Model Number --</option>
                    {catalogModelNumbers.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                    <option value="custom">-- Custom Model Number / Manual Entry --</option>
                  </select>
                </div>

                {isSbsCustomModel && (
                  <div className="form-group">
                    <label className="form-label">Custom Model Number</label>
                    <input
                      type="text"
                      className="form-input"
                      value={sbsForm.modelNumber}
                      onChange={e => setSbsForm({ ...sbsForm, modelNumber: e.target.value })}
                      placeholder="e.g. STAR-CUSTOM-100"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="form-group">
                <label className="form-label">Model Number *</label>
                <input
                  type="text"
                  className="form-input"
                  required
                  value={sbsForm.modelNumber}
                  onChange={e => setSbsForm({ ...sbsForm, modelNumber: e.target.value })}
                  placeholder="e.g. Model-123"
                />
              </div>
            )}

            {/* 5. Cascading Spec Section (Inner Size, Outer Size, Bending Radius, Design Type, VS, HS, SR) */}
            {isDragChain && (
              <div style={{ background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-light)', marginBottom: '16px' }}>

              {/* Row 1: Inner Size + Outer Size */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Inner Size</label>
                  {availableSbsInnerSizes.length > 0 ? (
                    <select
                      className="form-input"
                      value={sbsForm.size}
                      onChange={handleSbsInnerSizeChange}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">-- Choose Inner Size --</option>
                      {availableSbsInnerSizes.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={sbsForm.size}
                      onChange={e => setSbsForm({ ...sbsForm, size: e.target.value })}
                      placeholder="e.g. 35X140 OP"
                    />
                  )}
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.8rem' }}>Outer Size (Auto-Filled)</label>
                  <input
                    type="text"
                    className="form-input"
                    readOnly
                    value={sbsForm.description?.replace('Outer: ', '') || 'Auto-generated'}
                    style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--text-secondary)' }}
                  />
                </div>
              </div>

              {/* Row 2: Bending Radius (Only for Drag Chain) */}
              {isDragChain && (
                <div style={{ marginBottom: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.8rem' }}>Bending Radius</label>
                    <select
                      className="form-input"
                      value={sbsForm.radius}
                      onChange={e => setSbsForm({ ...sbsForm, radius: e.target.value })}
                      style={{ cursor: 'pointer' }}
                    >
                      <option value="">-- Select Radius --</option>
                      {availableSbsRadii.length > 0 ? (
                        availableSbsRadii.map(r => <option key={r} value={r}>{r}</option>)
                      ) : (
                        ['15', '25', '40', '50', '55', '75', '100', '125', '150', '175', '200', '225', '250', '300', '1000'].map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              )}
            </div>
            )}
            {/* 6. Quantity + Unit Price + Real-time Total Banner */}
            <div style={{ background: 'rgba(255,255,255,0.02)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border-light)', marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Quantity ({sbsForm.unit || 'pcs'}) *</label>
                  <input type="number" step="0.01" className="form-input" required min={0} value={sbsForm.quantity} onChange={e => setSbsForm({ ...sbsForm, quantity: Number(e.target.value) })} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Price / Rate for 1 {sbsForm.unit || 'pcs'} (₹)</label>
                  <input type="number" min={0} step="0.01" className="form-input" value={sbsForm.unitPrice || 0} onChange={e => setSbsForm({ ...sbsForm, unitPrice: Number(e.target.value) })} placeholder="e.g. 150" />
                </div>
              </div>

              <div style={{
                background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)',
                borderRadius: '8px', padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <span style={{ fontSize: '0.88rem', color: 'var(--text-primary)', fontWeight: 600 }}>
                  Total price of the product quantity:
                </span>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#22c55e' }}>
                  ₹{((sbsForm.quantity || 0) * (sbsForm.unitPrice || 0)).toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            {/* 7. Initial Sourcing Assignment / Assignee */}
            <div className="form-group">
              <label className="form-label">Initial Sourcing Assignment / Assignee *</label>
              <select
                className="form-input"
                value={sbsForm.assignedTo || 'unassigned'}
                onChange={e => setSbsForm({ ...sbsForm, assignedTo: e.target.value })}
              >
                <option value="unassigned">Unassigned</option>
                <option value="supervisor">Supervisor (Production Line)</option>
                <option value="sales">Sales Officer (Purchase Sourcing)</option>
                <option value="quality_checker">Quality Checker (QC Inspections)</option>
                <option value="store_manager">Store Manager (Godown Stock)</option>
                <option value="production_manager">Production Manager</option>
              </select>
            </div>

            {/* 8. Remarks */}
            <div className="form-group">
              <label className="form-label">Remarks</label>
              <textarea className="form-input" rows="2" value={sbsForm.remarks} onChange={e => setSbsForm({ ...sbsForm, remarks: e.target.value })} placeholder="Describe details..."></textarea>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowSbsModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Log'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

      {/* ADMIN EDIT BUILD JOB MODAL */}
      {showEditBuildModal && (
        <ModalOverlay onClose={() => setShowEditBuildModal(false)} title="Edit Manufacturing Build Job (Admin Only)">
          <form onSubmit={handleSaveEditBuild}>
            <div className="form-group">
              <label className="form-label">Product Name *</label>
              <input type="text" className="form-input" required value={editBuildForm.productName} onChange={e => setEditBuildForm({ ...editBuildForm, productName: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Order Quantity (pcs) *</label>
              <input type="number" className="form-input" required min={1} value={editBuildForm.orderQuantity} onChange={e => setEditBuildForm({ ...editBuildForm, orderQuantity: Number(e.target.value) })} />
            </div>
            <div className="form-group">
              <label className="form-label">Status *</label>
              <select className="form-input" value={editBuildForm.status} onChange={e => setEditBuildForm({ ...editBuildForm, status: e.target.value })}>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="delayed">Delayed</option>
                <option value="shortage_reported">Shortage Reported</option>
                <option value="completed">Completed</option>
                <option value="received">Received</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Estimated Completion Date</label>
              <input type="date" className="form-input" value={editBuildForm.estimatedDate} onChange={e => setEditBuildForm({ ...editBuildForm, estimatedDate: e.target.value })} />
            </div>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button type="button" className="btn btn-cancel" onClick={() => setShowEditBuildModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Saving...' : 'Save Changes'}</button>
            </div>
          </form>
        </ModalOverlay>
      )}

    </DashboardLayout>
  );
};


const ModalOverlay = ({ children, onClose, title }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
    display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
  }}>
    <div style={{
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '16px',
      width: '100%', maxWidth: '520px', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
      overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.5)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <h3 style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{title}</h3>
        <button onClick={onClose} title="Close" style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.4rem', cursor: 'pointer' }}><HiOutlineX /></button>
      </div>
      <div style={{ padding: '24px', overflowY: 'auto', overflowX: 'hidden', flex: 1 }}>
        {children}
      </div>
    </div>
  </div>
);

export default SalesDashboard;
